// src/pages/GuestOnboardingPage.tsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Note as NoteIcon,
  Schedule as ScheduleIcon,
  ChatBubble as ChatBubbleIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { UserRole } from '../types/chat';
import { isGuestProfileIncomplete } from '../shared/validators';

const GuestOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectGuest, user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = '#00AEEF';
  const accentHover = '#0095cf';
  const surfaceBg = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.94) 100%)';
  const surfaceBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(255, 255, 255, 0.18)';
  const primaryTextColor = isDark ? '#f8fafc' : '#1f2937';
  const secondaryTextColor = isDark ? 'rgba(248, 250, 252, 0.75)' : '#6b7280';
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [hasIncompleteGuests, setHasIncompleteGuests] = useState(false);
  const [checkingGuests, setCheckingGuests] = useState(true);
  const [incompleteGuest, setIncompleteGuest] = useState<{
    firstName: string;
    lastName: string;
    profileId: string;
  } | null>(null);
  const [roomCapacity, setRoomCapacity] = useState<number | null>(null);
  const [currentGuestCount, setCurrentGuestCount] = useState<number>(0);

  const hotelId = searchParams.get('hotelId');
  const reservationId = searchParams.get('reservationId');
  const profileId = searchParams.get('profileId');
  const surname = searchParams.get('surname');
  const redirectToChatParam = searchParams.get('redirectToChat') === 'true';
  // Yeni eklenen misafir için: AddGuestPage'den gelince bu set edilir; selectGuest soyad eşleşmesi için gerekli
  const guestLastName = searchParams.get('guestLastName');

  // Check if there are guests with incomplete information in the same room/reservation
  React.useEffect(() => {
    const checkIncompleteGuests = async () => {
      if (!hotelId || !reservationId) {
        setCheckingGuests(false);
        return;
      }

      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (!token) {
        setHasIncompleteGuests(true);
        setCheckingGuests(false);
        return;
      }

      try {
        // Get all guests in the same reservation (using guest-accessible endpoint)
        const usersResponse = await api.get('/users/reservation-guests', {
          params: { reservationId: reservationId }
        });
        
        const roomGuests = usersResponse.data || [];

        // Also fetch the reservation's room capacity (public endpoint, works pre/post login)
        try {
          const capacityResponse = await api.get('/auth/reservation-capacity', {
            params: { hotelId, reservationId },
          });
          setCurrentGuestCount(capacityResponse.data?.currentGuestCount ?? 0);
          setRoomCapacity(capacityResponse.data?.maxGuestCount ?? null);
        } catch (capacityErr) {
          console.warn('[GuestOnboardingPage] Failed to fetch reservation capacity:', capacityErr);
          // Fall back to counting locally
          const onlyGuests = (roomGuests || []).filter((g: any) => !g.role || g.role === 'guest' || g.role === 'GUEST');
          setCurrentGuestCount(onlyGuests.length);
        }
        
        // Helper function to check if a guest has incomplete information
        const isGuestIncomplete = (guest: any): boolean => {
          if (!guest || guest.role !== 'guest') return false;
          return isGuestProfileIncomplete(guest);
        };
        
        // Helper function to validate guest data is complete and valid
        const isValidIncompleteGuest = (guest: any): boolean => {
          if (!guest) return false;
          if (!isGuestIncomplete(guest)) return false; // Must be incomplete
          if (!guest.profileId) return false; // Must have profileId
          if (!guest.firstName && !guest.lastName) return false; // Must have at least one name
          return true;
        };
        
        // Find the first incomplete guest
        // Priority: 1. Current user (if incomplete), 2. Other guests
        // We use API data (roomGuests) for accuracy, not context user which might be stale
        let foundIncompleteGuest: { firstName: string; lastName: string; profileId: string } | null = null;
        
        // Find current user in the roomGuests list (by id or profileId)
        const currentUserInRoom = user 
          ? roomGuests.find((guest: any) => 
              (user.id && guest.id === user.id) || 
              (user.profileId && guest.profileId === user.profileId)
            )
          : null;
        
        // First check if current user (from API data) is incomplete
        if (currentUserInRoom && isValidIncompleteGuest(currentUserInRoom)) {
          foundIncompleteGuest = {
            firstName: currentUserInRoom.firstName || '',
            lastName: currentUserInRoom.lastName || '',
            profileId: currentUserInRoom.profileId,
          };
        } else {
          // Current user is complete (or not found), find first incomplete guest
          const incompleteGuests = roomGuests.filter((guest: any) => {
            if (!isValidIncompleteGuest(guest)) return false;
            // Exclude current user
            if (currentUserInRoom) {
              if (currentUserInRoom.id && guest.id === currentUserInRoom.id) return false;
              if (currentUserInRoom.profileId && guest.profileId === currentUserInRoom.profileId) return false;
            }
            return true;
          });
          
          if (incompleteGuests.length > 0) {
            const firstIncomplete = incompleteGuests[0];
            // Double-check the guest is valid before setting
            if (isValidIncompleteGuest(firstIncomplete)) {
              foundIncompleteGuest = {
                firstName: firstIncomplete.firstName || '',
                lastName: firstIncomplete.lastName || '',
                profileId: firstIncomplete.profileId,
              };
            }
          }
        }
        
        // Only set incomplete guest if we found a valid one
        if (foundIncompleteGuest && foundIncompleteGuest.profileId) {
          setIncompleteGuest(foundIncompleteGuest);
          setHasIncompleteGuests(true);
        } else {
          setIncompleteGuest(null);
          setHasIncompleteGuests(false);
        }
      } catch (error) {
        console.error('Error checking incomplete guests:', error);
        // On error, default to showing the button (safe fallback)
        setHasIncompleteGuests(true);
      } finally {
        setCheckingGuests(false);
      }
    };

    checkIncompleteGuests();
  }, [hotelId, reservationId, user]);

  const handleCompleteInfo = async () => {
    // Use the incomplete guest info we already found
    // If incompleteGuest.profileId matches current user, don't pass targetProfileId (complete own info)
    // Otherwise, pass targetProfileId to complete that guest's info
    let targetProfileId: string | null = null;
    
    if (incompleteGuest) {
      // Check if this is the current user
      if (user && user.profileId && incompleteGuest.profileId === user.profileId) {
        // Completing own info, don't pass targetProfileId
        targetProfileId = null;
      } else {
        // Completing another guest's info
        targetProfileId = incompleteGuest.profileId;
      }
    }
    
    const params = new URLSearchParams({
      hotelId: hotelId!,
      reservationId: reservationId!,
    });
    if (profileId) params.append('profileId', profileId);
    if (surname) params.append('surname', surname);
    if (targetProfileId) {
      params.append('targetProfileId', targetProfileId);
    }
    if (redirectToChatParam) {
      params.append('redirectToChat', 'true');
    }
    navigate(`/complete-guest-info?${params.toString()}`);
  };

  const handleAddNote = () => {
    const params = new URLSearchParams({
      hotelId: hotelId!,
      reservationId: reservationId!,
    });
    if (profileId) params.append('profileId', profileId);
    if (surname) params.append('surname', surname);
    if (redirectToChatParam) params.append('redirectToChat', 'true');
    navigate(`/add-reservation-note?${params.toString()}`);
  };

  const handleAddAnotherGuest = () => {
    if (!hotelId || !reservationId) return;
    const params = new URLSearchParams({
      hotelId,
      reservationId,
    });
    if (surname) params.append('surname', surname);
    if (redirectToChatParam) params.append('redirectToChat', 'true');
    navigate(`/add-guest?${params.toString()}`);
  };

  const isRoomFull = !!(
    typeof roomCapacity === 'number'
    && roomCapacity > 0
    && currentGuestCount >= roomCapacity
  );

  const handleAddCheckInTime = () => {
    const params = new URLSearchParams({
      hotelId: hotelId!,
      reservationId: reservationId!,
    });
    if (profileId) params.append('profileId', profileId);
    if (surname) params.append('surname', surname);
    if (redirectToChatParam) params.append('redirectToChat', 'true');
    navigate(`/add-check-in-time?${params.toString()}`);
  };

  const handleStartChat = async () => {
    if (!hotelId || !reservationId || !profileId) {
      // If we don't have the required info, fall back to login page
      const params = new URLSearchParams({
        hotelId: hotelId || '',
        reservationId: reservationId || '',
        redirectToChat: 'true',
      });
      if (surname) params.append('surname', surname);
      navigate(`/login?${params.toString()}`);
      return;
    }

    setIsLoadingChat(true);
    try {
      // Yeni eklenen misafir için guestLastName kullan (backend soyad eşleşmesi istiyor)
      const surnameForLogin = guestLastName || surname || '';
      const result = await selectGuest(hotelId, reservationId, profileId, surnameForLogin);
      
      // Navigate based on user role
      if (result.user?.role === UserRole.SUPER_ADMIN) {
        navigate('/super-admin');
      } else {
        // Try to get the user's chats and navigate to the first one
        try {
          const chatsResponse = await api.get('/chats');
          const chats = chatsResponse.data;
          if (chats && chats.length > 0) {
            // Navigate to the first chat
            navigate(`/dashboard/chat/${chats[0].id}`);
          } else {
            // No chats yet, just go to dashboard
            navigate('/dashboard');
          }
        } catch (chatError) {
          console.error('Error fetching chats:', chatError);
          // If error, just go to dashboard
          navigate('/dashboard');
        }
      }
    } catch (error: any) {
      console.error('Failed to auto-login:', error);
      // If auto-login fails, fall back to login page
      const params = new URLSearchParams({
        hotelId: hotelId!,
        reservationId: reservationId!,
        redirectToChat: 'true',
      });
      if (surname) params.append('surname', surname);
      navigate(`/login?${params.toString()}`);
    } finally {
      setIsLoadingChat(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          background: surfaceBg,
          backdropFilter: 'blur(18px)',
          border: surfaceBorder,
          color: primaryTextColor,
          boxShadow: isDark
            ? '0 25px 70px rgba(0, 0, 0, 0.65)'
            : '0 30px 80px rgba(15, 23, 42, 0.12)',
        }}
      >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: accentColor }}>
              {t('onboarding.successTitle') || 'Success!'}
            </Typography>
            <Typography variant="body2" sx={{ color: secondaryTextColor }}>
              {t('onboarding.successMessage') || 'Your information has been successfully updated. Thank you for your time.'}
            </Typography>
          </Box>

          {/* Action Buttons — önce ilave misafir (yazışmadan önce), sonra eksik bilgi vb. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleAddAnotherGuest}
              disabled={isRoomFull || !hotelId || !reservationId}
              sx={{
                py: 1.75,
                borderRadius: 3,
                borderColor: accentColor,
                borderWidth: 2,
                color: accentColor,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: 'rgba(0, 174, 239, 0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: accentHover,
                  borderWidth: 2,
                  backgroundColor: 'rgba(0, 174, 239, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 174, 239, 0.2)',
                },
                '&:disabled': {
                  borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)',
                  color: isDark ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                  backgroundColor: 'transparent',
                },
              }}
            >
              {isRoomFull
                ? (t('addGuest.roomFullLabel', {
                    current: currentGuestCount,
                    max: roomCapacity,
                    defaultValue: `Room full (${currentGuestCount}/${roomCapacity})`,
                  }))
                : (typeof roomCapacity === 'number' && roomCapacity > 0
                    ? `${t('onboarding.addAnotherGuest', {
                        defaultValue: 'Add another guest',
                      })} (${currentGuestCount}/${roomCapacity})`
                    : t('onboarding.addAnotherGuest', {
                        defaultValue: 'Add another guest',
                      }))}
            </Button>

            {!checkingGuests && hasIncompleteGuests && incompleteGuest && incompleteGuest.profileId && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleCompleteInfo}
                sx={{
                  py: 1.75,
                  borderRadius: 3,
                  borderColor: accentColor,
                  borderWidth: 2,
                  color: accentColor,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: 'rgba(0, 174, 239, 0.05)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: accentHover,
                    borderWidth: 2,
                    backgroundColor: 'rgba(0, 174, 239, 0.1)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 174, 239, 0.2)',
                  },
                }}
              >
                {(() => {
                  if (!incompleteGuest || !incompleteGuest.profileId) {
                    return t('onboarding.completeInfo') || 'Complete missing information';
                  }
                  const firstName = incompleteGuest.firstName || '';
                  const lastName = incompleteGuest.lastName || '';
                  if ((firstName || lastName)) {
                    return t('onboarding.completeInfoFor', {
                      firstName: firstName || '',
                      lastName: lastName || '',
                    }) || `Complete missing information for ${firstName} ${lastName}`.trim();
                  }
                  return t('onboarding.completeInfo') || 'Complete missing information';
                })()}
              </Button>
            )}

            <Button
              fullWidth
              variant="outlined"
              startIcon={<NoteIcon />}
              onClick={handleAddNote}
              sx={{
                py: 1.75,
                borderRadius: 3,
                borderColor: accentColor,
                borderWidth: 2,
                color: accentColor,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: 'rgba(0, 174, 239, 0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: accentHover,
                  borderWidth: 2,
                  backgroundColor: 'rgba(0, 174, 239, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 174, 239, 0.2)',
                },
              }}
            >
              {t('onboarding.addNote') || 'Add a note to your reservation'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={handleAddCheckInTime}
              sx={{
                py: 1.75,
                borderRadius: 3,
                borderColor: accentColor,
                borderWidth: 2,
                color: accentColor,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: 'rgba(0, 174, 239, 0.05)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: accentHover,
                  borderWidth: 2,
                  backgroundColor: 'rgba(0, 174, 239, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 174, 239, 0.2)',
                },
              }}
            >
              {t('onboarding.addCheckInTime') || 'Specify estimated arrival time'}
            </Button>

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={isLoadingChat ? <CircularProgress size={20} color="inherit" /> : <ChatBubbleIcon />}
              onClick={handleStartChat}
              disabled={isLoadingChat}
              sx={{
                mt: 2,
                py: 1.5,
                backgroundColor: accentColor,
                '&:hover': {
                  backgroundColor: accentHover,
                },
              }}
            >
              {t('onboarding.startChat') || 'Start chatting with us'}
            </Button>
          </Box>
        </Paper>
      </Container>
  );
};

export default GuestOnboardingPage;
